import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UpArrowRed, DownArrowRed } from 'src/assets/images';
import { useField, useFormikContext } from 'formik';
import './quantity-counter-input.scss';

export type QuantityCounterInputProps = {
  name: string;
  min: number;
  max: number;
  automationid: string;
  disabled?: boolean;
  className?: string;
  /**
   *  Optional function will be called when control value changes.
   *
   *  @param value - the new control value
   *  @returns false or undefined if the new control value should be
   *  discarded.
   *  @returns true otherwise.
   *
   * @param value
   */
  onModified?: (value: number) => boolean | undefined;
};

export const QuantityCounterInput = ({
  min,
  max,
  automationid,
  onModified,
  disabled,
  ...props
}: QuantityCounterInputProps) => {
  const [field] = useField(props);
  const { setFieldValue } = useFormikContext();

  const [modalOpen, setModalOpen] = useState(false);
  const numberRef = useRef<HTMLDivElement>(null);
  const dropDownRef = useRef<HTMLUListElement>(null);
  const numbers = Array(max - min + 1)
    .fill(0)
    .map((_, idx) => min + idx);

  const normalize = (value: number) => Math.min(Math.max(value, min), max);
  const valueMemo = useMemo(() => normalize(field.value), [field.value]);

  useEffect(() => {
    numberRef.current?.scrollTo(0, valueMemo * 22); // ~22px per number

    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [valueMemo, modalOpen]);

  const setValue = (value: number): void => {
    if (!onModified || onModified(normalize(value))) {
      setFieldValue(field.name, normalize(value));
    }
  };

  const valueChanged = (e) => {
    e.preventDefault();
    const newValue: number = parseInt(e.target.value) || valueMemo;
    setValue(newValue);
  };

  const handleModalClick = () => {
    setModalOpen(!modalOpen);
  };

  const checkIfClickedOutside = (e) => {
    if (modalOpen && dropDownRef.current && !dropDownRef.current.contains(e.target)) {
      setModalOpen(!modalOpen);
    }
  };

  return (
    <section className={`quantity-counter-container ${props?.className ? props.className : ''}`}>
      <p>Qty:</p>
      <input
        {...field}
        type="number"
        id={field.name}
        maxLength={2}
        min={min}
        max={max}
        disabled={disabled}
        value={valueMemo}
        onChange={(e) => {
          e.preventDefault();
          const valueNew = e.target.value;

          const regex = /^[1-9][0-9]*$/;
          if (valueNew && regex.test(valueNew)) {
            setValue(Number(valueNew));
          }
        }}
      />
      {!disabled && (
        <img src={modalOpen ? UpArrowRed : DownArrowRed} onClick={() => setModalOpen(!modalOpen)} />
      )}
      {modalOpen && !disabled && (
        <section className={numbers.length < 3 ? 'drop-down no-scrollbar' : 'drop-down'}>
          <ul
            className="drop-down-content"
            onChange={valueChanged}
            onClick={handleModalClick}
            ref={dropDownRef}
          >
            {numbers.map((number) => (
              <li
                value={number}
                key={number}
                onClick={() => setValue(number)}
                automationid={`${automationid}_${number}`}
              >
                {number}
              </li>
            ))}
          </ul>
        </section>
      )}
    </section>
  );
};
